'use client';
import { useCallback } from 'react';
import { Form, type FormSubmitHandler, useForm } from 'react-hook-form';

import type { FeedbackCategory } from '~entities/feedback/names';
import { Toaster } from '~features/toaster';
import { useUserId } from '~hooks/useUserId';
import { useTranslations } from '~i18n/hooks';
import { userApi } from '~services/api/user';
import {
	ButtonPrimary,
	RatingField,
	TextareaField,
} from '~shared/ui/molecules';
import { FormGrid } from '~theme/snippets';

import { Container } from './styled';
import type { FeedbackFormValues } from './types';

type Props = {
	onSubmit: () => void;
	onClick?: () => void;
	category: FeedbackCategory;
};

export const FeedbackForm = ({ onSubmit, onClick, category }: Props) => {
	const formT = useTranslations('Forms');
	const userId = useUserId();
	const [send, { isLoading }] = userApi.useSendUserFeedbackMutation();

	const handleSubmit = useCallback(
		(values: FeedbackFormValues) => {
			onClick?.();
			const body = {
				category,
				rate: Number(values.rating),
				text: values.feedback,
			};
			send({ userId, body })
				.unwrap()
				.then(() => {
					Toaster.show({
						titleKey: 'feedbackSentTitle',
						textKey: 'feedbackSentMessage',
					});

					onSubmit();
				})
				.catch(() => Toaster.showError({ textKey: 'generalErrorMessage' }));
		},
		[onClick, category, send, userId, onSubmit]
	);

	return (
		<PureForm
			isLoading={isLoading}
			onSubmit={handleSubmit}
			requiredError={formT('validationRequired')}
			maxLengthError={formT('validationMaxLengthReached')}
		/>
	);
};

type PureProps = {
	isLoading: boolean;
	requiredError: string;
	maxLengthError: string;
	onSubmit: (values: FeedbackFormValues) => unknown;
};

export const PureForm = ({
	isLoading,
	onSubmit,
	requiredError = 'Required Error',
	maxLengthError = 'Max Length Error',
}: PureProps) => {
	const {
		control,
		register,
		formState: { errors, isSubmitting, isValid, dirtyFields },
	} = useForm<FeedbackFormValues>({
		mode: 'onChange',
		reValidateMode: 'onChange',
	});

	const isDirtyAlt = !!Object.keys(dirtyFields).length;
	const handleSubmit: FormSubmitHandler<FeedbackFormValues> = useCallback(
		({ data }) => onSubmit(data),
		[onSubmit]
	);
	const loading = isSubmitting || isLoading;

	return (
		<Form className={FormGrid} control={control} onSubmit={handleSubmit}>
			<Container>
				<RatingField<FeedbackFormValues>
					name="rating"
					control={control}
					rules={{ required: requiredError }}
					disabled={loading}
				/>
				<div />
				<TextareaField
					placeholder="Share some details"
					{...register('feedback', {
						minLength: 0,
						maxLength: {
							value: 200,
							message: maxLengthError,
						},
					})}
					disabled={loading}
					error={errors.feedback?.message}
					rows={4}
				/>
				<ButtonPrimary
					width={280}
					type="submit"
					size="L"
					disabled={!isDirtyAlt || !isValid}
					loading={loading}
				>
					Submit Feedback
				</ButtonPrimary>
			</Container>
		</Form>
	);
};
